import React from 'react';
import PageTemplate from '../../components/Common/PageTemplate';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FileText, Users, Settings, Target, CheckCircle, ArrowRight } from 'lucide-react';

const ManagementScheme = () => {
  const { t } = useTranslation();

  const activities = [
    {
      icon: <Users className="w-12 h-12" />,
      title: t('management_scheme.activities.academic_affairs.title'),
      description: t('management_scheme.activities.academic_affairs.description'),
      color: 'blue'
    },
    {
      icon: <FileText className="w-12 h-12" />,
      title: t('management_scheme.activities.administrative_affairs.title'),
      description: t('management_scheme.activities.administrative_affairs.description'),
      color: 'green'
    },
    {
      icon: <Target className="w-12 h-12" />,
      title: t('management_scheme.activities.quality_assurance.title'),
      description: t('management_scheme.activities.quality_assurance.description'),
      color: 'purple'
    },
    {
      icon: <Settings className="w-12 h-12" />,
      title: t('management_scheme.activities.technical_support.title'),
      description: t('management_scheme.activities.technical_support.description'),
      color: 'orange'
    }
  ];

  const workflows = [
    {
      step: '1',
      title: t('management_scheme.workflows.step1.title'),
      description: t('management_scheme.workflows.step1.description')
    },
    {
      step: '2',
      title: t('management_scheme.workflows.step2.title'),
      description: t('management_scheme.workflows.step2.description')
    },
    {
      step: '3',
      title: t('management_scheme.workflows.step3.title'),
      description: t('management_scheme.workflows.step3.description')
    },
    {
      step: '4',
      title: t('management_scheme.workflows.step4.title'),
      description: t('management_scheme.workflows.step4.description')
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'from-blue-500 to-blue-600',
      green: 'from-green-500 to-green-600',
      purple: 'from-purple-500 to-purple-600',
      orange: 'from-orange-500 to-orange-600'
    };
    return colors[color] || colors.blue;
  };

  return (
    <PageTemplate
      title={t('management_scheme.title')}
      subtitle={t('management_scheme.subtitle')}
    >
      <div className="space-y-16">
        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto"
        >
          <p className="text-lg text-gray-700 leading-relaxed">
            {t('management_scheme.intro')}
          </p>
        </motion.div>

        {/* Unit Activities */}
        <div>
          <motion.h2
            className="text-3xl font-bold text-center text-gray-900 mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {t('management_scheme.unit_activities_title')}
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {activities.map((activity, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-t-4 border-transparent hover:border-t-4"
                style={{ borderTopColor: `var(--${activity.color}-500)` }}
              >
                <div className={`inline-flex items-center justify-center p-4 rounded-xl bg-gradient-to-br ${getColorClasses(activity.color)} text-white mb-6`}>
                  {activity.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{activity.title}</h3>
                <p className="text-gray-600 leading-relaxed">{activity.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Workflows */}
        <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl p-8 md:p-12">
          <motion.h2
            className="text-3xl font-bold text-center text-gray-900 mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            {t('management_scheme.workflows_title')}
          </motion.h2>

          <div className="relative">
            {/* Connecting Line */}
            <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-purple-500 hidden md:block"></div>

            <div className="space-y-8">
              {workflows.map((workflow, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative flex items-start gap-6"
                >
                  {/* Step Number */}
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg z-10">
                    {workflow.step}
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-shadow">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                      {workflow.title}
                      <ArrowRight className="w-5 h-5 text-blue-500" />
                    </h3>
                    <p className="text-gray-600">{workflow.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-center text-white"
        >
          <CheckCircle className="w-16 h-16 mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-4">{t('management_scheme.contact_title')}</h3>
          <p className="text-lg opacity-90 max-w-2xl mx-auto">
            {t('management_scheme.contact_description')}
          </p>
        </motion.div>
      </div>
    </PageTemplate>
  );
};

export default ManagementScheme; 